import React, { useState } from 'react'
import { Reactable } from '../../components/ui/reactable'
import { Eye,ChevronLeft} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { truncateText } from "../../components/common/CommonFunctions";
import { useNavigate,useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { cn } from '../../utils/tailwindUtils';
import { CUSTOMERSETCATEGORY} from '../../constants/api';
import { ModuleTitleCard } from '../../components/ui/cards';



const StatusBadge = ({ status, type }) => (
    <span className={cn(
        "px-3 py-1 text-sm font-medium rounded-full",
        type === 'stage' ? (
            status === 'Negotiation' ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" :
                status === 'Proposal' ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" :
                    "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300"
        ) : (
            status ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" :
                "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
        )
    )}>
        {status || (type === 'stage' ? 'Not Specified' : '') || (type === 'active' ? 'Active' : 'Inactive')}
    </span>
);

const StatusBadgesComponent = ({ extraData, currentFilter, onFilterChange }) => {
    const [showAllStatuses, setShowAllStatuses] = useState(true);

    return (
        <div className="flex flex-wrap gap-2 mb-2">
            {/* Message Badge */}
            <div
                className={cn(
                    "px-3 py-1 rounded-full text-sm font-medium border cursor-pointer transition-colors duration-200",
                    !currentFilter?.lead_stage
                        ? "bg-blue-600 text-white border-blue-700"
                        : "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800 hover:bg-blue-200"
                )}
                onClick={() => onFilterChange({})}
            >
                Filter based on status
            </div>

            <div
                className={cn(
                    "px-3 py-1 rounded-full text-sm font-medium border cursor-pointer transition-colors duration-200",
                    !currentFilter?.lead_stage
                        ? "bg-blue-600 text-white border-blue-700"
                        : "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800 hover:bg-blue-200"
                )}
                onClick={() => onFilterChange({})}
            >
                All
            </div>

            {/* Dynamically generated badges from status_count */}
            {(() => {
                if (!extraData?.status_count) return null;

                const statusCounts = extraData.status_count;
                let othersCount = 0;

                // Group similar statuses (case-insensitive, with variations)
                const groupedStatuses = {};

                Object.entries(statusCounts).forEach(([status, count]) => {
                    // Collect null and empty statuses into "others"
                    if (!status || status === 'null' || status === '') {
                        othersCount += count;
                        return;
                    }

                    const normalizedStatus = status.toLowerCase().trim();

                    // Handle common variations
                    let key = normalizedStatus;
                    let apiKey = status; // Keep original for API query

                    if (normalizedStatus.includes('disqualifed') ||
                        normalizedStatus.includes('dis-qualified') ||
                        normalizedStatus.includes('disqualified')) {
                        key = 'disqualified';
                        apiKey = 'disqualified';
                    } else if (normalizedStatus.includes('shown intrest') ||
                        normalizedStatus.includes('shown interest')) {
                        key = 'shown interest';
                        apiKey = 'shown interest';
                    } else if (normalizedStatus.includes('demo fixed') ||
                        normalizedStatus.includes('demo done')) {
                        key = 'demo done';
                        apiKey = 'demo done';
                    } else if (normalizedStatus.includes('no show') ||
                        normalizedStatus === 'no show') {
                        key = 'no show';
                        apiKey = 'no show';
                    } else if (normalizedStatus === 'contacted') {
                        key = 'contacted';
                        apiKey = 'contacted';
                    } else if (normalizedStatus === 'qualified') {
                        key = 'qualified';
                        apiKey = 'qualified';
                    } else if (normalizedStatus.includes('negotiation') ||
                        normalizedStatus === 'negotitation') {
                        key = 'negotiation';
                        apiKey = 'negotiation';
                    } else if (normalizedStatus.includes('closed won') ||
                        normalizedStatus === 'won') {
                        key = 'closed won';
                        apiKey = 'closed won';
                    } else if (normalizedStatus.includes('new lead')) {
                        key = 'new lead';
                        apiKey = 'new lead';
                    } else if (normalizedStatus.includes('proposal')) {
                        key = 'proposal';
                        apiKey = 'proposal';
                    } else if (normalizedStatus.includes('pipeline')) {
                        key = 'pipeline';
                        apiKey = 'pipeline';
                    } else if (normalizedStatus.includes('language barrier')) {
                        key = 'language barrier';
                        apiKey = 'language barrier';
                    }

                    if (!groupedStatuses[key]) {
                        groupedStatuses[key] = {
                            displayName: key.charAt(0).toUpperCase() + key.slice(1),
                            count: 0,
                            apiKey: apiKey
                        };
                    }
                    groupedStatuses[key].count += count;
                });

                // Add "Others" category if there are null/empty statuses
                if (othersCount > 0) {
                    groupedStatuses['others'] = {
                        displayName: 'Others',
                        count: othersCount,
                        apiKey: 'others'
                    };
                }

                // Define a logical order for display
                const statusOrder = [
                    'new lead',
                    'contacted',
                    'shown interest',
                    'qualified',
                    'demo done',
                    'proposal',
                    'negotiation',
                    'closed won',
                    'pipeline',
                    'language barrier',
                    'no show',
                    'disqualified',
                    'others'
                ];

                // Sort by defined order, then alphabetically for others
                const sortedStatuses = Object.entries(groupedStatuses)
                    .sort(([keyA], [keyB]) => {
                        const indexA = statusOrder.indexOf(keyA);
                        const indexB = statusOrder.indexOf(keyB);

                        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                        if (indexA !== -1) return -1;
                        if (indexB !== -1) return 1;

                        return keyA.localeCompare(keyB);
                    });

                // Color mapping for common statuses
                const getStatusColor = (statusKey, isActive) => {
                    const activeColors = {
                        'new lead': 'bg-gray-600 text-white border-gray-700',
                        'contacted': 'bg-purple-600 text-white border-purple-700',
                        'qualified': 'bg-yellow-600 text-white border-yellow-700',
                        'proposal': 'bg-indigo-600 text-white border-indigo-700',
                        'negotiation': 'bg-orange-600 text-white border-orange-700',
                        'closed won': 'bg-green-600 text-white border-green-700',
                        'disqualified': 'bg-red-600 text-white border-red-700',
                        'demo done': 'bg-blue-600 text-white border-blue-700',
                        'shown interest': 'bg-teal-600 text-white border-teal-700',
                        'no show': 'bg-rose-600 text-white border-rose-700',
                        'pipeline': 'bg-cyan-600 text-white border-cyan-700',
                        'language barrier': 'bg-amber-600 text-white border-amber-700',
                        'others': 'bg-gray-700 text-white border-gray-800',
                        'default': 'bg-blue-600 text-white border-blue-700'
                    };

                    const inactiveColors = {
                        'new lead': 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200',
                        'contacted': 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800 hover:bg-purple-200',
                        'qualified': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800 hover:bg-yellow-200',
                        'proposal': 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-200',
                        'negotiation': 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-800 hover:bg-orange-200',
                        'closed won': 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800 hover:bg-green-200',
                        'disqualified': 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800 hover:bg-red-200',
                        'demo done': 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800 hover:bg-blue-200',
                        'shown interest': 'bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300 border-teal-200 dark:border-teal-800 hover:bg-teal-200',
                        'no show': 'bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800 hover:bg-rose-200',
                        'pipeline': 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800 hover:bg-cyan-200',
                        'language barrier': 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800 hover:bg-amber-200',
                        'others': 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-300',
                        'default': 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200'
                    };

                    return isActive ? (activeColors[statusKey] || activeColors.default) : (inactiveColors[statusKey] || inactiveColors.default);
                };

                // Determine how many badges to show initially (3-5 based on available count)
                const initialCount = Math.min(4, sortedStatuses.length);

                // Get badges to show based on state
                const badgesToShow = showAllStatuses
                    ? sortedStatuses
                    : sortedStatuses.slice(0, initialCount);
                const hiddenCount = sortedStatuses.length - initialCount;

                return (
                    <>
                        {/* Render badges to show */}
                        {badgesToShow.map(([key, { displayName, count, apiKey }]) => {
                            const isActive = currentFilter?.lead_stage === apiKey;
                            return (
                                <div
                                    key={key}
                                    onClick={() => onFilterChange({ lead_stage: apiKey })}
                                    className={cn(
                                        getStatusColor(key, isActive),
                                        "px-3 py-1 rounded-full text-sm font-medium border cursor-pointer transition-colors duration-200"
                                    )}
                                    title={`${count} ${displayName}`}
                                >
                                    {displayName.replace(/\b\w/g, l => l.toUpperCase())}: {count}
                                </div>
                            );
                        })}

                        {/* Show "Show More" button if there are hidden badges and not showing all */}
                        {!showAllStatuses && hiddenCount > 0 && (
                            <button
                                type="button"
                                onClick={() => setShowAllStatuses(true)}
                                className="bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 
                                         text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 
                                         px-3 py-1 rounded-full text-sm font-medium border border-gray-200 
                                         dark:border-gray-700 transition-colors duration-200 cursor-pointer
                                         flex items-center gap-1"
                            >
                                +{hiddenCount} more
                            </button>
                        )}

                        {/* Show "Show Less" button when showing all and there are more than initial count */}
                        {showAllStatuses && sortedStatuses.length > initialCount && (
                            <button
                                type="button"
                                onClick={() => setShowAllStatuses(false)}
                                className="bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 
                                         text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 
                                         px-3 py-1 rounded-full text-sm font-medium border border-gray-200 
                                         dark:border-gray-700 transition-colors duration-200 cursor-pointer"
                            >
                                Show less
                            </button>
                        )}
                    </>
                );
            })()}

            {/* Total Count Badge - Always show at the end */}
            <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium border border-blue-300 dark:border-blue-700">
                Total: {extraData?.total_count||0}
            </div>
        
        </div>
    );
};

const FunnelCategoryDetails = () => {
    const sessionStorageRole = sessionStorage.getItem("login_role");
    const navigate = useNavigate();
    const {state} = useLocation();
    const [extraData,setExtraData] = useState(null)
    const [filter,setFilter]=useState({})
    const [funnelCategoryData, setFunnelCategoryData] = useState([])
    const [totalRecords, setTotalRecords] = useState(0);
    console.log(extraData);

    const CustomersListColumns = [
        { accessor: "customer_name", Header: "Customer Name", sortable: true },
        {
            accessor: "customer_type",
            Header: "Customer Type",
            sortable: true,
        },
        {
            accessor: "contact_number",
            Header: "Contact Number",
            visible: false,
        },
        {
            accessor: "current_assignee",
            Header: "Current Assignee",
            sortable: true,
            Cell: ({ value }) => (
                <span className="text-sm text-gray-600 dark:text-gray-400">
                    {value || 'Not assigned'}
                </span>
            )
        },
        // {
        //     accessor: "customer_description",
        //     Header: "Description",
        //     sortable: false,
        //     Cell: ({ value }) => truncateText(value, 25, "300px", "balance", "No Description")
        // },
        
        {
            accessor: "status",
            Header: "Follow up Status",
            sortable: false,
            Cell: ({ value, row }) => {
                const leadStage = row?.lead_stage;

                return (
                    <StatusBadge
                        status={leadStage}
                        type="stage"
                    />
                );
            },
        },
        ...sessionStorageRole === 'third_party' ? [] : [
            {
                accessor: "action",
                Header: "Actions",
                sortable: false,
                fixed: true,
                align: 'right',
                Cell: ({ row }) => {

                    const handleOpenView = () => {
                        navigate('/customers/customerdetails', { state: { formEdit: true, row: row } })
                    }


                    return (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                onClick={handleOpenView}
                            >
                                <Eye className="h-4 w-4" />
                            </Button>

                        </div>
                    );
                },
            },
        ]
    ];
  return (
    <div>
        <ModuleTitleCard
            Icon={ChevronLeft}
            title={`${state?.category.category_name}`}
            subtitle="This section displays customer names, types, and related details grouped by funnel"
            totalRecords={totalRecords}
            iconVisibility={false}
        />

        <StatusBadgesComponent
            extraData={extraData}
            currentFilter={filter}
            onFilterChange={setFilter}
        />
        <Reactable
            columns={CustomersListColumns}
            setTotalCount={setTotalRecords}
            data={funnelCategoryData}
            setData={setFunnelCategoryData}
            url={CUSTOMERSETCATEGORY}
            setWholeResponse={setExtraData}
            serverSide={true}
            rowSize={"xs"}
            filter={{category_id:state?.category.category_id,...filter}}
            isCSVRequired={true}
            isPDFRequired={false}
            isDraftRequired={false}
            isDateRequired={false}
            bulkUploadRequired={false}
        />
    </div>
  )
}

export default FunnelCategoryDetails